import { useQuery } from "@tanstack/react-query";
import { offer } from "@/generated";
import { OpenAPI } from "@/generated/core/OpenAPI";
import { request as __request } from "@/generated/core/request";

const NUMERIC_ID = /^\d+$/;

export function useFetchOffer(
  offerIdOrRef: string,
  options?: { enabled?: boolean; kind?: "id" | "ref"; ref?: string },
) {
  const explicitRef = options?.ref?.trim() || undefined;

  return useQuery({
    queryKey: [
      "offer",
      offerIdOrRef,
      options?.kind ?? "auto",
      explicitRef ?? "",
    ],
    queryFn: async () => {
      const retailerId = Number(process.env.NEXT_PUBLIC_RETAILER_ID) || 0;

      // Lookup type is explicit when `kind` is provided; otherwise fall back to
      // the heuristic (numeric → id, non-numeric → ref). The explicit form is
      // required because a reference can itself be numeric.
      const lookupById =
        options?.kind === "id" ||
        (options?.kind === undefined && NUMERIC_ID.test(offerIdOrRef));

      // The backend lookup is /offers/{offer_id} with an optional offer_ref
      // query param, so id and ref can be sent together:
      //  - id only  → offer_id={id}
      //  - ref only → offer_id=0 & offer_ref={ref}
      //  - both     → offer_id={id} & offer_ref={ref}
      const offerIdPath = lookupById ? Number(offerIdOrRef) : 0;
      const offerRef = lookupById ? explicitRef : offerIdOrRef;

      const response = await __request(OpenAPI, {
        method: "GET",
        url: "/offers/{offer_id}",
        path: { offer_id: offerIdPath },
        query: { offer_ref: offerRef, retailer_id: retailerId },
      });
      return response as unknown as offer;
    },
    enabled: options?.enabled !== false && !!(offerIdOrRef || explicitRef),
    gcTime: 0, // Don't cache the response
    staleTime: 0, // Always consider data stale to refetch
  });
}
